import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveLine } from '@nivo/line';
import { getChartColors, getNivoCommonProps } from '../../utils/nivoTheme';
import {
  formatAxisNumber,
  TooltipWrapper
} from '../../utils/chartUtils';
import i18n from '../../i18n/config.js';
import './CashflowChart.css';

/**
 * Monday-based week key for a date string (YYYY-MM-DD).
 * @param {string} dateStr
 * @returns {string}
 */
const weekKey = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  const day = d.getDay();
  const daysToMonday = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - daysToMonday);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayNum}`;
};

/**
 * One x-axis tick date per week (first available day in each week).
 * @param {Array<{date: string}>} days
 * @returns {string[]}
 */
const weeklyTickValues = (days) => {
  if (!days || days.length === 0) return [];

  const ticks = [];
  let previousWeek = null;
  for (const day of days) {
    const key = weekKey(day.date);
    if (key !== previousWeek) {
      ticks.push(day.date);
      previousWeek = key;
    }
  }
  return ticks;
};

/**
 * Contract backlog line chart for the Overview page.
 * @param {Object} props
 * @param {Array} props.data - Contract backlog day records from API
 */
const ContractBacklogChart = ({ data }) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  const commonProps = getNivoCommonProps();
  const chartColors = getChartColors();

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyCompact = (value) => {
    if (value === null || value === undefined) return '';
    const formatted = formatAxisNumber(value, 2);
    return `${formatted} kr`;
  };

  const lineData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [{
      id: 'remaining_backlog',
      data: data.map(day => ({
        x: day.date,
        y: day.remaining_backlog
      }))
    }];
  }, [data]);

  const tickValues = useMemo(() => weeklyTickValues(data), [data]);

  const { yScaleMin, yScaleMax } = useMemo(() => {
    if (!data || data.length === 0) {
      return { yScaleMin: 0, yScaleMax: 100000 };
    }

    const values = data.map(day => day.remaining_backlog).filter(v => v != null);
    if (values.length === 0) {
      return { yScaleMin: 0, yScaleMax: 100000 };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const padding = Math.max(range * 0.1, Math.abs(max) * 0.05);

    return {
      yScaleMin: min - padding,
      yScaleMax: max + padding
    };
  }, [data]);

  const CustomTooltip = ({ slice }) => {
    if (!slice || !slice.points || slice.points.length === 0) {
      return null;
    }

    const date = slice.points[0].data.x;
    const dayData = data.find(d => {
      const dayDateStr = typeof d.date === 'string' ? d.date : d.date.toISOString().split('T')[0];
      const compareDateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      return dayDateStr === compareDateStr;
    });

    if (!dayData) return null;

    return (
      <div style={{
        background: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-light)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--spacing-4)',
        minWidth: '200px',
        maxWidth: '400px'
      }}>
        <div style={{
          color: 'var(--color-text-secondary)',
          fontWeight: 500,
          marginBottom: 'var(--spacing-1)',
          fontSize: 'var(--font-size-xs)'
        }}>
          {t('overview.tooltipBacklogScope')}
        </div>
        <div style={{
          color: 'var(--color-text-primary)',
          fontWeight: 600,
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-base)'
        }}>
          {new Date(date).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          whiteSpace: 'nowrap'
        }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('overview.remainingBacklog')}:</span>
          <strong>{formatCurrency(dayData.remaining_backlog)}</strong>
        </div>

        {dayData.events
          .filter(event => event.event !== 'no_event')
          .map((event, index) => (
            <div key={index}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-2)',
                fontSize: 'var(--font-size-sm)',
                whiteSpace: 'nowrap'
              }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {t(`overview.backlogEventNames.${event.event}`, event.event.replace(/_/g, ' '))}:
                </span>
                <strong style={{
                  color: event.amount < 0 ? 'var(--color-outgoing)' : event.amount > 0 ? 'var(--color-incoming)' : 'var(--color-text-primary)'
                }}>
                  {formatCurrency(event.amount)}
                </strong>
              </div>

              {event.projects && event.projects.length > 0 && (
                <div style={{
                  marginLeft: 'var(--spacing-4)',
                  marginTop: 'var(--spacing-1)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-tertiary)'
                }}>
                  {event.projects.map((project, projectIndex) => (
                    <div key={projectIndex} style={{ marginBottom: 'var(--spacing-1)' }}>
                      • {project.project_name}: {formatCurrency(project.amount)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    );
  };

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className="cashflow-chart-container">
      <div className="cashflow-chart-wrapper">
        <div className="cashflow-line-chart">
          <ResponsiveLine
            {...commonProps}
            data={lineData}
            margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: yScaleMin, max: yScaleMax, nice: false }}
            areaBaselineValue={yScaleMin}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              tickValues,
              format: (value) => {
                const date = new Date(`${value}T00:00:00`);
                return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
              }
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              format: (value) => formatCurrencyCompact(value)
            }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            enableArea={true}
            areaOpacity={0.15}
            useMesh={true}
            enableSlices="x"
            sliceTooltip={({ slice }) => {
              const firstSeriesData = lineData[0]?.data || [];
              const currentX = slice.points[0]?.data?.x;
              const pointIndex = firstSeriesData.findIndex(p => String(p.x) === String(currentX));

              return (
                <TooltipWrapper dataIndex={pointIndex} dataLength={firstSeriesData.length}>
                  <CustomTooltip slice={slice} />
                </TooltipWrapper>
              );
            }}
            isInteractive={true}
            enableCrosshair={true}
            colors={[chartColors[1]]}
            legends={[]}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractBacklogChart;
